/**
 * Human-readable text from axios/API failures.
 * Supports Garaaz AllExceptionsFilter (`errorMsg`) and Nest defaults (`message`),
 * string or string[] for each. Ignores axios's generic "Request failed with status code N"
 * when the server did not send a message.
 */

const AXIOS_GENERIC_STATUS_MESSAGE =
  /^Request failed with status code \d+$/i;

function stringifyBackendField(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? t : null;
  }
  if (Array.isArray(value)) {
    const parts = value
      .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
      .map((x) => x.trim());
    return parts.length > 0 ? parts.join("\n") : null;
  }
  return null;
}

/**
 * @param error - Typically an AxiosError from a failed request
 * @param fallback - Shown when no usable server or client message exists
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (typeof error !== "object" || error === null) {
    return fallback;
  }

  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };

  const data = err.response?.data;
  if (data && typeof data === "object") {
    const fromErrorMsg = stringifyBackendField(data.errorMsg);
    if (fromErrorMsg) {
      return fromErrorMsg;
    }
    const fromMessage = stringifyBackendField(data.message);
    if (fromMessage) {
      return fromMessage;
    }
  }

  const axiosMsg = err.message;
  if (
    typeof axiosMsg === "string" &&
    axiosMsg.trim() &&
    !AXIOS_GENERIC_STATUS_MESSAGE.test(axiosMsg.trim())
  ) {
    return axiosMsg.trim();
  }

  return fallback;
}
